import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPaperPlane, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

/**
 * Send / clear-all action row for the inbox chat composer. Extracted from
 * {@link useAddInboxChat} so that large hook stays under the file-length gate. When the messages
 * wait for the sender's pending invitation to be accepted, Send reads "Schedule for acceptance"
 * (PREC-4216): nothing goes out now.
 */
export const ChatSendBar: FunctionComponent<{
	draftCount: number;
	sendableCount: number;
	isSendDisabled: boolean;
	isSending: boolean;
	waitsForAcceptance?: boolean;
	onClearAll: () => void;
	onSend: () => void;
}> = ({
	draftCount,
	sendableCount,
	isSendDisabled,
	isSending,
	waitsForAcceptance,
	onClearAll,
	onSend,
}) => {
	const { t } = useTranslation();
	const sendTitle = waitsForAcceptance
		? t("inbox.pendingInvitation.scheduleAction")
		: sendableCount <= 1
			? "Send message"
			: `Send ${sendableCount} messages`;

	return (
		<div className="w-fit flex gap-2">
			{draftCount > 1 ? (
				<div className="w-fit">
					<Button
						color={ButtonColor.ERROR}
						image={<Icon className="mr-2" icon={faTrash} />}
						size={ButtonSize.XS}
						title="Clear all reply drafts"
						onClick={onClearAll}
					/>
				</div>
			) : null}
			<Button
				color={ButtonColor.BRAND}
				disabled={isSendDisabled}
				image={<Icon className="mr-2" icon={faPaperPlane} />}
				isLoading={isSending}
				size={ButtonSize.XS}
				title={sendTitle}
				onClick={onSend}
			/>
		</div>
	);
};
